/**
 * MSR Portal — Schedule view.
 *
 * Workstream 4 Phase 1. Lists the events the current user works: the event's Sales Reps list (the source of
 * truth, migration 414), the legacy events.msr_id, or a CTA / attribution on a sales_position they own. Data
 * scoped server-side; the page never asks for someone else's events.
 *
 * Mobile-first single-column layout. Two views (MSR QA notes 2026-10-19, item 1):
 *   - Upcoming (default): today through +90 days, today first. A day that is still running stays here.
 *   - Past: every day that has ended, most recent first, in its own view rather than continuing under Today, and
 *     a past event saved as 'scheduled' reads "Ended" instead of "Scheduled".
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import MsrTabs from "../../components/msr/MsrTabs";
import MsrCalendarSubscribe from "../../components/msr/MsrCalendarSubscribe";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../types/auth.types";
import {
  getEventDaysFor,
  getMsrSchedule,
  type MsrScheduleRow,
} from "../../services/msrPortal.service";
import { groupByDay, dayLabel, deviceToday } from "../../utils/msrEventDay";
import {
  dayOfLabel,
  groupEventDays,
  scheduleDayEntries,
  type EventDayEntry,
} from "../../utils/eventServiceDays";
import EventStatusBadge from "../../components/events/EventStatusBadge";
import { useEventDayHours } from "../../hooks/useEventDayHours";
import {
  formatClock,
  formatDayLabel,
  resolveDailyHours,
  type DayHours,
} from "../../utils/eventDayHours";
import { getEventZoneAbbrev } from "../../utils/eventTime";
import SalesCalendarView from "../../components/salesCalendar/SalesCalendarView";
import type { CalendarEvent } from "../../types/salesCalendar.types";
import {
  formatEventLocation,
  formatEventSpan,
  formatRepDays,
  formatServiceDate,
  eventZoneFromLocation,
  wallClockDate,
  zoneAbbreviation,
} from "../../utils/msrEventFormat";

/** Date/time in the EVENT's timezone: that day's hours for a day of a multi-day event, else the event's span. */
const formatEventTime = (
  entry: EventDayEntry<MsrScheduleRow>,
  dailyHours: DayHours[] | null,
): string => {
  const row = entry.row;
  if (!row.start_time) {
    return row.start_date ? formatServiceDate(row.start_date) : "Date TBD";
  }
  // Hours set for each day (migration 2026-10-27-1120) win over the uniform daily clock derived from start/end.
  const day = dailyHours?.find((d) => d.service_date === entry.date);
  if (day) {
    const zone = getEventZoneAbbrev(
      entry.windowStart ?? row.start_time,
      row.timezone ?? undefined,
    );
    return `${formatDayLabel(day.service_date)} · ${formatClock(day.opens_at)} – ${formatClock(day.closes_at)}${zone ? ` ${zone}` : ""}`;
  }
  if (entry.dayCount > 1 && entry.windowStart) {
    return formatEventSpan(entry.windowStart, entry.windowEnd, row.timezone);
  }
  return formatEventSpan(row.start_time, row.end_time, row.timezone);
};

type ScheduleCard = EventDayEntry<MsrScheduleRow> & {
  start_time: string | null;
  timezone: string | null;
};

type ScheduleView = "upcoming" | "past";

const entryKey = (e: { row: { event_id: string }; date: string }): string =>
  `${e.row.event_id}:${e.date}`;

const MsrSchedule: React.FC = () => {
  const { user } = useAuth();
  const isMsr = user?.role === UserRole.MSR;
  const [view, setView] = useState<ScheduleView>("upcoming");
  // Item 6: a calendar next to the list (the same component and container as My Availability's calendar).
  const [layout, setLayout] = useState<"list" | "calendar">("list");
  const [calDate, setCalDate] = useState<Date>(() => new Date());
  const [calView, setCalView] = useState<string>("month");
  const navigate = useNavigate();
  const includePast = view === "past";

  const { data, isLoading, error } = useQuery({
    queryKey: ["msr-schedule", includePast],
    queryFn: () => getMsrSchedule(includePast, 90),
  });

  const rows = useMemo(() => data ?? [], [data]);

  // GS-03: a multi-day event is listed under EACH of its days ("Day 1 of 2"). Date-model events use their
  // event_days rows; any other event's days come from its start/end in its own time zone. A per-day rep sees only
  // the days they work.
  const eventIds = useMemo(() => rows.map((r) => r.event_id).sort(), [rows]);
  const { data: dayRows } = useQuery({
    queryKey: ["msr-schedule-event-days", eventIds],
    queryFn: () => getEventDaysFor(eventIds),
    enabled: eventIds.length > 0,
  });

  // Hours each day for this rep's multi-day events (one request; single-day events are skipped).
  const { data: dayHoursMap } = useEventDayHours(
    rows.map((r) => ({
      id: r.event_id,
      start_time: r.start_time,
      end_time: r.end_time,
      timezone: r.timezone,
    })),
  );

  // Grouped by event-local day, today pinned first so a multi-event day is clustered and easy to route between.
  const groups = useMemo(() => {
    const today = deviceToday();
    const days = groupEventDays(dayRows ?? []);
    // Upcoming = the days the default schedule keeps (not ended, or still running). Past = everything else.
    const upcomingKeys = new Set(
      scheduleDayEntries(rows, days, today, false).map(entryKey),
    );
    const entries: ScheduleCard[] = scheduleDayEntries(rows, days, today, true)
      .filter((e) =>
        view === "past"
          ? !upcomingKeys.has(entryKey(e)) && e.date !== ""
          : upcomingKeys.has(entryKey(e)),
      )
      .map((e) => ({
        ...e,
        start_time: e.windowStart ?? e.row.start_time,
        timezone: e.row.timezone,
      }));
    // groupByDay orders past days most recent first, which is what the Past view wants.
    return groupByDay(entries, today, (e) => e.date);
  }, [rows, dayRows, view]);

  // Calendar entries: one per event day, at the event's local time (zone from its location, item 4).
  const calendarEvents: CalendarEvent[] = useMemo(
    () =>
      groups.flatMap((g) =>
        g.rows.flatMap((entry) => {
          const row = entry.row;
          const startIso = entry.windowStart ?? row.start_time;
          if (!startIso && !entry.date) {
            return [];
          }
          const zone = eventZoneFromLocation(row);
          const timed = !!startIso;
          const start = timed
            ? wallClockDate(startIso as string, zone.timezone)
            : new Date(`${entry.date}T00:00:00`);
          const endIso = entry.windowStart ? entry.windowEnd : row.end_time;
          const end =
            timed && endIso
              ? wallClockDate(endIso, zone.timezone)
              : new Date(start.getTime() + 60 * 60 * 1000);
          const dayOf = dayOfLabel(entry);
          const abbr = timed
            ? zoneAbbreviation(startIso as string, zone.timezone)
            : "";
          const ev: CalendarEvent = {
            id: `msr-${row.event_id}-${entry.date}`,
            title:
              (row.title ?? "Untitled event") +
              (dayOf ? ` (${dayOf})` : "") +
              (abbr ? ` · ${abbr}` : ""),
            start,
            end: end > start ? end : new Date(start.getTime() + 3600000),
            allDay: !timed,
            resource: {
              type: "event",
              repName: "",
              repColor: "#3b82f6",
              positionId: "",
              originalData: row,
              eventStatus: row.status ?? undefined,
              eventType: row.event_type ?? undefined,
              locationName: row.location_name ?? undefined,
            },
          };
          return [ev];
        }),
      ),
    [groups],
  );

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">My Schedule</h1>
        <p className="text-sm text-gray-600">
          {view === "past"
            ? "Events you were assigned to that have ended, most recent first."
            : "Events assigned to you for the next 90 days."}
        </p>
      </header>

      <MsrTabs active="schedule" />

      <div
        className="inline-flex rounded-md border border-gray-300 bg-white p-0.5"
        role="tablist"
        aria-label="Which events"
      >
        {(["upcoming", "past"] as const).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            data-testid={`msr-schedule-view-${v}`}
            onClick={() => setView(v)}
            className={`min-h-[40px] rounded px-4 text-sm font-medium ${
              view === v
                ? "bg-primary-600 text-white"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            {v === "upcoming" ? "Upcoming" : "Past events"}
          </button>
        ))}
      </div>

      <div className="flex gap-1" role="group" aria-label="Layout">
        {(["list", "calendar"] as const).map((l) => (
          <button
            key={l}
            type="button"
            aria-pressed={layout === l}
            data-testid={`msr-schedule-layout-${l}`}
            onClick={() => setLayout(l)}
            className={`rounded px-3 py-1 text-xs font-medium ${
              layout === l
                ? "bg-gray-800 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {l === "list" ? "List" : "Calendar"}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="text-center text-gray-500 py-12">Loading…</div>
      )}

      {error !== null && error !== undefined && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-900">
          {(error as Error).message}
        </div>
      )}

      {layout === "calendar" && !isLoading && (
        <div
          className="flex h-[600px] min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-2"
          data-testid="msr-schedule-calendar"
        >
          <SalesCalendarView
            events={calendarEvents}
            currentDate={calDate}
            currentView={calView}
            onNavigate={setCalDate}
            onView={setCalView}
            onSelectEvent={(ev) => {
              const row = ev.resource.originalData as { event_id?: string };
              if (row.event_id) {
                navigate(`/msr/events/${row.event_id}`);
              }
            }}
          />
        </div>
      )}

      {layout === "list" && !isLoading && groups.length === 0 && (
        <div className="text-center text-gray-600 py-12 px-4">
          <p className="font-medium text-gray-900">
            {view === "past"
              ? "No past events."
              : "No upcoming events assigned."}
          </p>
          {view === "upcoming" && (
            <p className="mt-2 text-sm">
              Ask your manager to confirm your event assignments.
            </p>
          )}
        </div>
      )}

      <div className="space-y-5">
        {(layout === "list" ? groups : []).map((group) => (
          <section key={group.date}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {group.date === "tbd" ? "Date TBD" : dayLabel(group.date)}
              {group.rows.length > 1 && (
                <span className="ml-1 font-normal normal-case text-gray-400">
                  · {group.rows.length} events
                </span>
              )}
            </h2>
            <div className="space-y-3">
              {group.rows.map((entry) => {
                const row = entry.row;
                const dayOf = dayOfLabel(entry);
                return (
                  <Link
                    key={`${row.event_id}:${entry.date}`}
                    to={`/msr/events/${row.event_id}`}
                    className="block bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:border-primary-400 transition-colors"
                    data-testid="msr-schedule-card"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900">
                        {row.title ?? "Untitled event"}
                      </h3>
                      {row.status !== null && row.status !== undefined && (
                        /* Shared badge: derives "Happening now" from the event's own
                           timestamps instead of trusting the hand-set status. */
                        <EventStatusBadge
                          event={row}
                          className="shrink-0"
                          pastScheduledAsEnded={view === "past"}
                        />
                      )}
                    </div>
                    {dayOf !== "" && (
                      <p
                        className="mt-1 text-xs font-medium text-primary-700"
                        data-testid="msr-schedule-day-of"
                      >
                        {dayOf}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-gray-700">
                      {formatEventTime(
                        entry,
                        resolveDailyHours(
                          entry.row,
                          dayHoursMap?.get(row.event_id),
                        ),
                      )}
                    </p>
                    {formatEventLocation(
                      row.location_name,
                      row.location_address,
                    ) !== "" && (
                      <p className="mt-1 text-xs text-gray-500">
                        {formatEventLocation(
                          row.location_name,
                          row.location_address,
                        )}
                      </p>
                    )}
                    {row.sales_rep_days && row.sales_rep_days.length > 0 && (
                      <p className="mt-1 text-xs text-gray-700">
                        Your days: {formatRepDays(row.sales_rep_days)}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      {(row.sales_rep_role === "lead" ||
                        (row.via_msr_id && !row.via_sales_reps)) && (
                        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                          Lead rep
                        </span>
                      )}
                      {row.sales_rep_role === "assigned" && (
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800">
                          Sales rep
                        </span>
                      )}
                      {row.via_cta && (
                        <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">
                          {row.owned_cta_count} CTA
                          {row.owned_cta_count === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* MSR item 5: private ICS subscription link (the RPC serves active MSRs only; admins previewing this page
          have no feed). */}
      {isMsr && <MsrCalendarSubscribe />}
    </div>
  );
};

export default MsrSchedule;
